import * as React from 'react';
// import * as ReactDom from 'react-dom';
import * as d3 from 'd3';
import { PVABadge } from '../../components';
import { User, UserStyle } from '../../models';
import './QuadrantGrid.css';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { UserStore, FilterStore } from '../../stores';
import './BadgeOverlayFix.css';

export class QuadrantGridData {
    public label: string;
    public value: number;
    public accommodate: number;
    public quadrant: number;
    public pos: any;
    public user: User;
    public additionalClass: string;
}

export class QuadrantGridCategory {
    public label: string;
}

interface QuadrantGridProps {
    data: QuadrantGridData[];
    one: QuadrantGridCategory;
    two: QuadrantGridCategory;
    three: QuadrantGridCategory;
    four: QuadrantGridCategory;
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

interface QuadrantGridState {
    data: QuadrantGridData[];
}

@inject('UserStore', 'FilterStore') @observer
export class QuadrantGrid extends React.Component<QuadrantGridProps, QuadrantGridState> {
    private svgContainer: any;
    private quadrantGridContainer: any;

    constructor(props: QuadrantGridProps) {
        super(props);

        this.state = {
            data: []
        };
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    componentDidMount() {
        this.buildInclinationChart();
        this.updateInclinationChart();
    }

    componentWillReceiveProps(nextProps: QuadrantGridProps) {
        this.setState({ data: nextProps.data }, this.updateInclinationChart);
    }

    buildInclinationChart() {
        const element = this.quadrantGridContainer; // ReactDom.findDOMNode(this);
        if (!element) {
            return;
        }

        this.svgContainer = d3.select(element).append('svg')
            .attr('width', element.clientWidth)
            .attr('height', element.clientHeight);

        // var borderSize: number = 18;
        var quadrantSize = element.clientWidth * 0.45;
        var midMark = element.clientWidth * 0.5;
        var middleSize = element.clientWidth * 0.1;
        var middleSizeWithPadding = middleSize * 0.6;
        var middleSizePadding = middleSize - middleSizeWithPadding;
        var middleSizeHalf = middleSize * 0.5;
        // var halfMiddle = (middleSize * 0.9) * 0.5;

        var quadrantBG = 'rgba(0, 34, 62, 0.15)';

        // more pronounced section background
        const backgroundGroup = this.svgContainer.append('g');
        var quadrantOne = backgroundGroup.append('g');
        quadrantOne.append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', quadrantSize)
            .attr('height', quadrantSize)
            .attr('fill', quadrantBG);

        var quadrantTwo = backgroundGroup.append('g');
        quadrantTwo.append('rect')
            .attr('x', midMark + middleSizeHalf)
            .attr('y', 0)
            .attr('width', quadrantSize)
            .attr('height', quadrantSize)
            .attr('fill', quadrantBG);

        var quadrantThree = backgroundGroup.append('g');
        quadrantThree.append('rect')
            .attr('x', midMark + middleSizeHalf)
            .attr('y', midMark + middleSizeHalf)
            .attr('width', quadrantSize)
            .attr('height', quadrantSize)
            .attr('fill', quadrantBG);

        var quadrantFour = backgroundGroup.append('g');
        quadrantFour.append('rect')
            .attr('x', 0)
            .attr('y', midMark + middleSizeHalf)
            .attr('width', quadrantSize)
            .attr('height', quadrantSize)
            .attr('fill', quadrantBG);

        var halfSize = middleSizeWithPadding / 2.0;
        var lengthOfRot = Math.sqrt(halfSize * halfSize + halfSize * halfSize); // a^2 + b^2 = c^2

        // Arrows
        // top
        backgroundGroup.append('rect')
            .attr('x', midMark - middleSizeWithPadding * 0.5)
            .attr('y', halfSize)
            .attr('width', middleSizeWithPadding)
            .attr('height', quadrantSize + (middleSizePadding * 0.4))
            .attr('fill', 'rgba(22, 61, 90, 1.0)');

        // bottom
        backgroundGroup.append('rect')
            .attr('x', midMark - middleSizeWithPadding * 0.5)
            .attr('y', midMark)
            .attr('width', middleSizeWithPadding)
            .attr('height', element.clientHeight / 2.0 - halfSize)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');

        // left
        backgroundGroup.append('rect')
            .attr('x', halfSize)
            .attr('y', midMark - middleSizeWithPadding * 0.5)
            .attr('width', quadrantSize + (middleSizePadding * 0.5))
            .attr('height', middleSizeWithPadding)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');

        // right
        backgroundGroup.append('rect')
            .attr('x', midMark)
            .attr('y', midMark - middleSizeWithPadding * 0.5)
            .attr('width', quadrantSize + (middleSizePadding * 0.5))
            .attr('height', middleSizeWithPadding)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');

        var arrowX = midMark;
        var arrowY = halfSize + 2; // midMark - (middleSize * 0.5) - 1 + (middleSizePadding * 0.4);
        
        // Top arrow
        this.svgContainer.append('rect')
            .attr('width', lengthOfRot)
            .attr('height', lengthOfRot)
            .attr('transform', 'rotate(45, ' + arrowX + ', ' + arrowY + ')')
            .attr('x', arrowX - lengthOfRot * 0.5)
            .attr('y', arrowY - lengthOfRot * 0.5)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');

        // Bottom arrow
        arrowY = element.clientHeight - halfSize - 2; // midMark + (middleSize * 0.5) + 1 - (middleSizePadding * 0.4);
        this.svgContainer.append('rect')
            .attr('width', lengthOfRot)
            .attr('height', lengthOfRot)
            .attr('transform', 'rotate(45, ' + arrowX + ', ' + arrowY + ')')
            .attr('x', arrowX - lengthOfRot * 0.5)
            .attr('y', arrowY - lengthOfRot * 0.5)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');
        
        // Left arrow
        arrowY = midMark;
        arrowX = halfSize + 2;
        this.svgContainer.append('rect')
            .attr('width', lengthOfRot)
            .attr('height', lengthOfRot)
            .attr('transform', 'rotate(45, ' + arrowX + ', ' + arrowY + ')')
            .attr('x', arrowX - lengthOfRot * 0.5)
            .attr('y', arrowY - lengthOfRot * 0.5)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');
    
    // Right arrow
        arrowY = midMark;
        arrowX = element.clientWidth - halfSize - 2; // midMark + (middleSize * 0.5) + 1 - (middleSizePadding * 0.4);
        this.svgContainer.append('rect')
            .attr('width', lengthOfRot)
            .attr('height', lengthOfRot)
            .attr('transform', 'rotate(45, ' + arrowX + ', ' + arrowY + ')')
            .attr('x', arrowX - lengthOfRot * 0.5)
            .attr('y', arrowY - lengthOfRot * 0.5)
            .attr('fill', 'rgba(22, 61, 90, 1.0)');
        
        {
            var charName = this.props.one.label || 'LOGIC GUIDED';
            var logicGuided = this.svgContainer.append('text').text(charName)
                .attr('font-size', '140%')
                .attr('font-family', 'Open Sans')
                .attr('font-weight', 'bold')
                .attr('text-align', 'right')
                .attr('text-transform', 'uppercase')
                .attr('fill', '#899dac')
                .attr('text-anchor', 'middle');

            var logicGuidedNode = logicGuided.node();
            var logicGuidedNodeBounds = logicGuidedNode.getBBox();
            
            var logicGuidedTextX = midMark + (logicGuidedNodeBounds.height / 4.0);
            var logicGuidedTextY = logicGuidedNodeBounds.width / 2.0 + (quadrantSize * 0.1);
            logicGuided
                .attr('x', logicGuidedTextX)
                .attr('y', logicGuidedTextY)
                .attr('transform', 'rotate(270, ' + logicGuidedTextX + ', ' + logicGuidedTextY + ')');
        }

        {
            var charName2 = this.props.two.label || 'VALUES GUIDED';
            var valuesGuided = this.svgContainer.append('text').text(charName2)
                .attr('font-size', '140%')
                .attr('font-family', 'Open Sans')
                .attr('font-weight', 'bold')
                .attr('text-align', 'right')
                .attr('text-transform', 'uppercase')
                .attr('fill', '#899dac')
                .attr('text-anchor', 'middle');

            var valuesGuidedNode = valuesGuided.node();
            var valuesGuidedNodeBounds = valuesGuidedNode.getBBox();
            
            var valuesGuidedTextX = midMark + (valuesGuidedNodeBounds.height / 4.0);
            var valuesGuidedTextY = element.clientHeight - valuesGuidedNodeBounds.width / 2.0 - (quadrantSize * 0.1);
            valuesGuided
                .attr('x', valuesGuidedTextX)
                .attr('y', valuesGuidedTextY)
                .attr('transform', 'rotate(270, ' + valuesGuidedTextX + ', ' + valuesGuidedTextY + ')');
        }

        {
            var charName3 = this.props.three.label || 'CONCRETE';
            var concrete = this.svgContainer.append('text').text(charName3)
                .attr('font-size', '140%')
                .attr('font-family', 'Open Sans')
                .attr('font-weight', 'bold')
                .attr('text-align', 'right')
                .attr('text-transform', 'uppercase')
                .attr('fill', '#899dac')
                .attr('text-anchor', 'middle');

            var concreteNode = concrete.node();
            var concreteNodeBounds = concreteNode.getBBox();
            
            var concreteTextX = (quadrantSize * 0.1) + (concreteNodeBounds.width / 2.0);
            var concreteTextY = (element.clientHeight / 2.0) + (concreteNodeBounds.height / 5.0);
            concrete
                .attr('x', concreteTextX)
                .attr('y', concreteTextY);
        }

        {
            var charName4 = this.props.four.label || 'ABSTRACT';
            var abstract = this.svgContainer.append('text').text(charName4)
                .attr('font-size', '140%')
                .attr('font-family', 'Open Sans')
                .attr('font-weight', 'bold')
                .attr('text-align', 'right')
                .attr('text-transform', 'uppercase')
                .attr('fill', '#899dac')
                .attr('text-anchor', 'middle');

            var abstractNode = abstract.node();
            var abstractNodeBounds = abstractNode.getBBox();
            
            var abstractTextX = element.clientWidth - (quadrantSize * 0.1) - (abstractNodeBounds.width / 2.0);
            var abstractTextY = (element.clientHeight / 2.0) + (abstractNodeBounds.height / 5.0);
            abstract
                .attr('x', abstractTextX)
                .attr('y', abstractTextY);
        }
        
        this.state.data.filter((d: QuadrantGridData) => {
            return d.quadrant === 0;
        }).map((d: QuadrantGridData) => {
            d.pos = this.addUserQuadrantOne(quadrantOne, d, quadrantSize, quadrantSize, quadrantSize, quadrantSize);
        });

        this.state.data.filter((d: QuadrantGridData) => {
            return d.quadrant === 1;
        }).map((d: QuadrantGridData) => {
            d.pos = this.addUserQuadrantTwo(quadrantTwo, d, quadrantSize, quadrantSize, midMark + middleSizeHalf, quadrantSize);
        });

        this.state.data.filter((d: QuadrantGridData) => {
            return d.quadrant === 2;
        }).map((d: QuadrantGridData) => {
            d.pos = this.addUserQuadrantThree(quadrantFour, d, quadrantSize, quadrantSize, quadrantSize, midMark + middleSizeHalf);
        });

        this.state.data.filter((d: QuadrantGridData) => {
            return d.quadrant === 3;
        }).map((d: QuadrantGridData) => {
            d.pos = this.addUserQuadrantFour(quadrantThree, d, quadrantSize, quadrantSize, midMark + middleSizeHalf, midMark + middleSizeHalf);
        });

        this.setState({
            data: this.state.data
        });
    }

    addUserQuadrantOne(svgContainer: any, data: QuadrantGridData, width: number, height: number, x0: number, y0: number) {            
        
        var x: number = width / 2.0;
        var y: number = height / 2.0;

        var xPerc = data.value / 100.0;
        var yPerc = data.accommodate / 100.0;
        xPerc *= xPerc;

        var magicX = width; // 3.569;
        var magicY = height; // 2.32;

        var dotSize = 10;

        x = x0 - Math.min(width - dotSize, Math.max(0 + dotSize, magicX * xPerc));
        y = y0 - Math.min(height - dotSize, Math.max(0 + dotSize, magicY * yPerc));

        return { x: x, y: y};
    }

    addUserQuadrantTwo(svgContainer: any, data: QuadrantGridData, width: number, height: number, x0: number, y0: number) {            
        
        var x: number = width / 2.0;
        var y: number = height / 2.0;

        var xPerc = data.value / 100.0;
        var yPerc = data.accommodate / 100.0;
        xPerc *= xPerc;

        var magicX = width; // 3.569;
        var magicY = height; // 2.32;

        var dotSize = 10;

        x = x0 + Math.min(width - dotSize, Math.max(0 + dotSize, magicX * xPerc));
        y = y0 - Math.min(height - dotSize, Math.max(0 + dotSize, magicY * yPerc));

        // x = Math.min(229, Math.max(21, 3.569 * (65 - (data.value - 35)) + 9));
        // y = Math.min(497, Math.max(297, 298 + 2.32 * data.accomadate - 6));

        return { x: x, y: y};
    }

    addUserQuadrantThree(svgContainer: any, data: QuadrantGridData, width: number, height: number, x0: number, y0: number) {            
        
        var x: number = width / 2.0;
        var y: number = height / 2.0;

        var xPerc = data.value / 100.0;
        var yPerc = data.accommodate / 100.0;
        xPerc *= xPerc;

        var magicX = width; // 3.569;
        var magicY = height; // 2.32;

        var dotSize = 10;

        x = x0 - Math.min(width - dotSize, Math.max(0 + dotSize, magicX * xPerc));
        y = y0 + Math.min(height - dotSize, Math.max(0 + dotSize, magicY * yPerc));

        // x = Math.max(292, 287 + Math.min(214, 3.569 * (data.value - 35) - 6));
        // y = Math.min(497, Math.max(297, 298 + 2.32 * data.accomadate - 6));

        return { x: x, y: y};
    }

    addUserQuadrantFour(svgContainer: any, data: QuadrantGridData, width: number, height: number, x0: number, y0: number) {            
        
        var x: number = width / 2.0;
        var y: number = height / 2.0;

        var xPerc = data.value / 100.0;
        var yPerc = data.accommodate / 100.0;
        xPerc *= xPerc;

        var magicX = width; // 3.569;
        var magicY = height; // 2.32;

        var dotSize = 10;

        x = x0 + Math.min(width - dotSize, Math.max(0 + dotSize, magicX * xPerc));
        y = y0 + Math.min(height - dotSize, Math.max(0 + dotSize, magicY * yPerc));

        // x = Math.max(292, 287 + Math.min(214, 3.569 * (data.value - 35) - 6));
        // y = Math.min(224, Math.max(23, 2.32 * (100 - data.accomadate) + 6));

        return { x: x, y: y};
    }

    updateInclinationChart() {
        if (this.svgContainer) {
            this.svgContainer.remove();
        }
        this.buildInclinationChart();
    }

    render() {
        var q1c = this.props.data.filter((d: QuadrantGridData) => { return d.quadrant === 0; }).length;
        var q2c = this.props.data.filter((d: QuadrantGridData) => { return d.quadrant === 1; }).length;
        var q3c = this.props.data.filter((d: QuadrantGridData) => { return d.quadrant === 2; }).length;
        var q4c = this.props.data.filter((d: QuadrantGridData) => { return d.quadrant === 3; }).length;
        var q1 = 100.0 * (q1c / this.props.data.length);
        var q2 = 100.0 * (q2c / this.props.data.length);
        var q3 = 100.0 * (q3c / this.props.data.length);
        var q4 = 100.0 * (q4c / this.props.data.length);
        if (isNaN(q1)) { q1 = 0; }
        if (isNaN(q2)) { q2 = 0; }
        if (isNaN(q3)) { q3 = 0; }
        if (isNaN(q4)) { q4 = 0; }

        return (
            <div className="flex-box">
                <div>
                    <div className="quadrant-grid-bg">
                        <div className="quadrant-grid-container" ref={(e) => this.quadrantGridContainer = e}>
                            <div className="quadrant-grid" />
                            <div className="quadrant-grid-title one">
                                {q1.toFixed(0)}%
                            </div>
                            <div className="quadrant-grid-title two">
                                {q2.toFixed(0)}%
                            </div>
                            <div className="quadrant-grid-title three">
                                {q3.toFixed(0)}%
                            </div>
                            <div className="quadrant-grid-title four">
                                {q4.toFixed(0)}%
                            </div>
                            {
                                this.state.data.map((d: QuadrantGridData, ind: number) => {
                                    if (!d.pos || !this.hasValidStyle(d.user)) {
                                        return <div key={`user-${ind}`} />;
                                    }
                                    const styleName = this.getStyleName(d.user);
                                    const styleColor = this.getStyleColor(styleName);
                                    const initials = (d.user.firstName[0] || '') + (d.user.lastName[0] || '');
                                    return <div key={`person-${ind}`} className={`quadrant-person ${d.additionalClass}`} style={{'top': d.pos.y, 'left': d.pos.x, '--badge-color': styleColor} as any}>
                                        <div className="person" style={{ fontWeight: 'bold', fontSize: '11px' }}>
                                            {initials}
                                            <div className="inner-content pva-badge-small person-popup" style={{color: 'var(--badge-color)'}}>
                                                <div className="popup-offset">
                                                    <div className="inner-content-bg">
                                                        <span className="name">{d.user && d.user.firstName} {d.user && d.user.lastName} {d.user.userStyles && d.user.userStyles.length > 0 && d.user.userStyles[0].overridden && '*'}</span>
                                                        <div className="badge-container">
                                                            {this.PVABadge(d.user, ind)}
                                                        </div>
                                                    </div>
                                                    <div className="triangle" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>;
                                })
                            }
                        </div>
                    </div>
                </div>
                <div className="team-grid-data-list-wrapper">
                    <div>
                        <label>Users</label>
                        <ul className="user-list">
                            {
                                this.state.data
                                    .filter(x => x.user)
                                    .sort(this.userSort)
                                    .map((quadrantGridData: QuadrantGridData, index: number) => {
                                        return <li key={`user-${quadrantGridData.user.id}`} className="person pva-badge-small" onMouseEnter={() => this.addHoverClass(quadrantGridData.user.id)} onMouseLeave={() => this.removeHoverClass(quadrantGridData.user.id)}>
                                            <span className="text">{quadrantGridData.user.lastName}, {quadrantGridData.user.firstName}</span>
                                            {this.PVABadge(quadrantGridData.user, index)}
                                        </li>;
                                    })
                            }
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    addHoverClass(userId: number) {
        let { data } = this.state;
        let quadrantDataIndex = data.findIndex(x => {
            return x.user.id === userId;
        });

        if (quadrantDataIndex >= 0) {
            // first remove pull-to-front from everyone
            data.forEach(x => x.additionalClass = '');

            // then set the new classes
            data[quadrantDataIndex].additionalClass = 'pull-to-front hovered-user';
            this.setState({data: data});
        }
    }

    removeHoverClass(userId: number) {
        let { data } = this.state;
        let quadrantDataIndex = data.findIndex(x => {
            return x.user.id === userId;
        });

        if (quadrantDataIndex >= 0) {
            data[quadrantDataIndex].additionalClass = '';
            this.setState({data: data});
        }
    }

    userSort(a: QuadrantGridData, b: QuadrantGridData) {
        if (a.user && b.user) {
            if (a.user.lastName > b.user.lastName) {
                return 1;
            } else if (a.user.lastName < b.user.lastName) {
                return -1;
            }
            if (a.user.firstName > b.user.firstName) {
                return 1;
            } else if (a.user.firstName < b.user.firstName) {
                return -1;
            }
        }
        return 0;
    }

    private hasValidStyle(user: User): boolean {
        if (!user || !user.userStyles) {
            return false;
        }

        if (!this.surveyVersion) {
            return user.userStyles.length > 0;
        }

        var surveyVersionId = this.surveyVersion.id;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersionID === surveyVersionId;
        });

        if (userStyle) {
            return true;
        }

        return user.userStyles.length > 0;
    }

    private getStyleName(user: User): string {
        if (!user || !user.userStyles) {
            return '';
        }

        var surveyVersionId = this.surveyVersion ? this.surveyVersion.id : 0;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersionID === surveyVersionId;
        });

        if (userStyle) {
            return userStyle.style.name;
        }

        if (user.userStyles.length > 0) {
            return user.userStyles[0].style.name;
        }

        return '';
    }

    private getStyleColor(styleName: string): string {
        if (!styleName) {
            return 'var(--grey)';
        }
        return `var(--pva-${styleName.toLowerCase()})`;
    }

    private PVABadge(user: User, index: number) {
        if (user === undefined) {
            return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
        }
        if (!this.survey || !user.userStyles || !this.surveyVersion) {
            return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
        }

        var surveyVersionId = this.surveyVersion.id;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersionID === surveyVersionId;
        });

        if (userStyle) {
            return <PVABadge key={`pvabadge-${index}`} style={userStyle.style.name} badge={userStyle.style.name[0]} />;
        }

        if (!userStyle && user.userStyles.length > 0) {
            return <PVABadge key={`pvabadge-${index}`} style={user.userStyles[0].style.name} badge={user.userStyles[0].style.name[0]} />;
        }

        return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
    }
    
}


// WEBPACK FOOTER //
// ./src/components/charts/QuadrantGrid.tsx